import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { toast } from "@/shared/widgets/toast";
import { UserEditDialog } from "./UserEditDialog";
import { UsersTableImproved } from "./UsersTableImproved";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import ProfilesService from "@/features/profiles/services/profiles/profileService";
import { IUser } from "@/features/profiles/services/profiles/types";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { Toggle } from "@/shared/ui/toggle";
import { Check, Search, Filter, Crown, Mail } from "lucide-react";
import { cn } from "@/shared/lib/utils";


export default function Users() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  
  // Estado local para paginação e filtros
  const [page, setPage] = useState(1);
  const [vipFilter, setVipFilter] = useState<boolean | undefined>(undefined);
  const [subscribedFilter, setSubscribedFilter] = useState<boolean | undefined>(undefined);
  const [pageSize, setPageSize] = useState(10);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const queryParams = useMemo(() => ({
    search: debouncedSearchQuery,
    page,
    vip: vipFilter,
    subscriber: subscribedFilter,
    limit: pageSize,
  }), [debouncedSearchQuery, page, vipFilter, subscribedFilter, pageSize]);

  const { data: paginatedUsers, isLoading } = useCustomQuery({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.getProfiles,
    parameter: queryParams,
  });

  const { mutate: toggleVip } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.toggleVip,
    onSuccess: () => {
      toast.success("Status VIP atualizado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["get-profiles"] });
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar VIP", error?.message || "Ocorreu um erro inesperado.");
    },
  });

  const handleVipToggle = ({ id, vip, vipExpiresAt }: { id: string, vip: boolean, vipExpiresAt: string }) => {
    toggleVip({ id, vip, vipExpiresAt });
  };

  // Reset page to 1 quando filtros ou busca mudarem
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, vipFilter, subscribedFilter]);

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const renderFilterToggle = (
    label: string,
    value: boolean | undefined,
    onChange: (value: boolean | undefined) => void,
    icon: React.ReactNode
  ) => (
    <Toggle
      variant="outline"
      aria-label={`Filtrar por ${label}`}
      pressed={value !== undefined}
      onPressedChange={(pressed) => onChange(pressed ? true : undefined)}
      className={cn(
        "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all duration-200",
        value !== undefined ? 'bg-primary text-primary-foreground' : 'bg-[#222222] border-white/10 text-white hover:bg-[#333333]'
      )}
    >
      {value !== undefined && <Check className="mr-2 h-4 w-4" />}
      {icon}
      <span className="ml-2">{label}</span>
    </Toggle>
  );

  const clearFilters = () => {
    setVipFilter(undefined);
    setSubscribedFilter(undefined);
    setSearchQuery("");
  };

  const hasActiveFilters = vipFilter !== undefined || subscribedFilter !== undefined || searchQuery;

  return (
    <PageContent title="Usuários">
      <div className="min-h-screen bg-[#1A1A1A] space-y-6">

        {/* Filtros e Busca */}
        <Card className="bg-[#222222] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              Filtros e Busca
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Barra de Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Pesquise por e-mail ou nome do usuário..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#1E1E1E] border-[#333333] text-white placeholder:text-gray-400 pl-10"
              />
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3 items-center">
              {renderFilterToggle("VIP", vipFilter, setVipFilter, <Crown className="w-4 h-4" />)}
              {renderFilterToggle("Assinante", subscribedFilter, setSubscribedFilter, <Mail className="w-4 h-4" />)}
              
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30"
                >
                  Limpar Filtros
                </Button>
              )}
            </div>

            {/* Indicador de Filtros Ativos */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2">
                {searchQuery && (
                  <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                    Busca: "{searchQuery}"
                  </Badge>
                )}
                {vipFilter !== undefined && (
                  <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                    VIP: {vipFilter ? 'Sim' : 'Não'}
                  </Badge>
                )}
                {subscribedFilter !== undefined && (
                  <Badge variant="secondary" className="bg-green-500/20 text-green-400 border-green-500/30">
                    Assinante: {subscribedFilter ? 'Sim' : 'Não'}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lista de Usuários */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              Usuários
            </h2>
          </div>

          <UsersTableImproved
            users={paginatedUsers?.data || []}
            isLoading={isLoading}
            onVipToggle={handleVipToggle}
            pagination={
              paginatedUsers
                ? {
                  currentPage: page,
                  totalPages: paginatedUsers.totalPage,
                  totalItems: paginatedUsers.totalItems,
                  pageSize,
                  onPageChange: handlePageChange,
                  onPageSizeChange: (size) => {
                    setPageSize(size);
                    setPage(1);
                  },
                  pageSizeOptions: [10, 20, 50, 100],
                  showPageSizeOptions: true,
                  showTotalItems: true,
                  syncWithQueryParams: false,
                }
                : undefined
            }
          />
        </div>
      </div>

      <UserEditDialog
        user={selectedUser}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </PageContent>
  );
}
